<script setup lang="ts">
import type { NavigationItemType } from '@/types/NavigationItemType';
import { NavigationKeyEnum } from '@/types/enums/NavigationKeyEnum';
import { useNavigationActivityStore } from '@/stores/navigationActivityStore';
import type { RouteLocationRaw } from 'vue-router';
import {
    BBadge, BPrimaryMenuIcon, BListItem,
} from '@/design-system';

type PropsType = {
    item: NavigationItemType;
    rail?: boolean;
};

const props = defineProps<PropsType>();
const emit = defineEmits<{
    expandRail: [];
}>();

const navigationActivityStore = useNavigationActivityStore();

const getRouteTo = (to: string | RouteLocationRaw) => {
    return typeof to === 'string' ? { name: to } : to;
};

// Remove onClick from activatorProps - we'll handle it ourselves
const filterActivatorProps = (activatorProps: Record<string, unknown>) => {
    const { onClick: _, ...rest } = activatorProps;

    return rest;
};

type VuetifyClickHandler = ((e: MouseEvent) => void) | undefined;

const handleActivatorClick = (event: MouseEvent, isOpen: boolean, vuetifyOnClick: VuetifyClickHandler) => {
    const target = event.target as HTMLElement;
    const isExpandIcon = target.closest('.v-list-item__append');

    // Arrow click - only toggle, never navigate
    if (isExpandIcon) {
        event.preventDefault();
        event.stopPropagation();
        vuetifyOnClick?.(event);

        return;
    }

    // Text/icon click on CLOSED group - open it via Vuetify
    if (!isOpen) {
        vuetifyOnClick?.(event);

        if (props.rail) {
            emit('expandRail');
        }
    }

    // Text/icon click on OPEN group - do nothing (don't close)
};

defineOptions({ inheritAttrs: false });
</script>

<template>
    <VListGroup
        v-bind="$attrs"
        :value="item.key"
    >
        <template #activator="{ isOpen, props: activatorProps }">
            <BListItem
                v-bind="filterActivatorProps(activatorProps)"
                :to="item.to ? getRouteTo(item.to) : undefined"
                :title="item.title"
                :disabled="item.disabled"
                density="compact"
                @click="
                    (event: MouseEvent) =>
                        handleActivatorClick(event, isOpen, activatorProps.onClick as VuetifyClickHandler)
                "
            >
                <template #prepend>
                    <BPrimaryMenuIcon
                        v-if="item.icon"
                        :icon="item.icon"
                        :icon-component="item.iconComponent"
                        :icon-component-width="item.iconComponentWidth"
                        :icon-component-height="item.iconComponentHeight"
                        :icon-component-color="item.iconComponentColor"
                    />
                </template>
                <template
                    v-if="item.navigationKey && navigationActivityStore.hasDot(item.navigationKey as NavigationKeyEnum)"
                    #append
                >
                    <span
                        v-if="navigationActivityStore.getActivity(item.navigationKey as NavigationKeyEnum)?.text"
                        class="navigation-activity-text"
                        :style="{
                            borderColor: `rgb(var(--v-theme-${navigationActivityStore.getActivity(item.navigationKey as NavigationKeyEnum)?.color ?? 'error'}))`,
                        }"
                    >
                        {{ navigationActivityStore.getActivity(item.navigationKey as NavigationKeyEnum)?.text }}
                    </span>
                    <BBadge
                        v-else
                        dot
                        :color="
                            navigationActivityStore.getActivity(item.navigationKey as NavigationKeyEnum)?.color ??
                                'ai-red'
                        "
                        inline
                    />
                </template>
            </BListItem>
        </template>

        <BListItem
            v-for="child in item.children"
            :key="child.to || child.title"
            :to="getRouteTo(child.to)"
            :title="child.title"
            :disabled="child.disabled"
            density="compact"
        />
    </VListGroup>
</template>

<style scoped lang="scss">
.navigation-activity-text {
    font-size: 0.7em;
    line-height: 1;
    padding: 2px 6px;
    border: 1px solid;
    border-radius: 8px;
    white-space: nowrap;
    font-weight: 400;
    color: rgba(var(--v-theme-grey-700));
}
</style>
